import { ReactNode, useEffect } from 'react';
import { TValidatorHash } from 'core/parseValidators';
import { IFormValue } from 'FormContext';
import useValidation, { IUseValidationProps } from '../useValidation';
import connectSectionToContext from './connectSectionToContext';

export interface IFormSectionProps<D = any>
  extends Partial<IFormValue>,
    Pick<IUseValidationProps, 'initialErrors' | 'initialData'> {
  children:
    | ReactNode
    | ((props: Pick<IFormSectionProps, 'initialData'> & IFormValue) => JSX.Element);
  /** The name of the form section */
  name: string;
  /** The validators for the data in the form section */
  validators?: TValidatorHash<D>;
}

function FormSection<D = any>({
  children,
  errors,
  initialData,
  initialErrors,
  name,
  registerSelfValidatingChild,
  unregisterSelfValidatingChild,
  ...rest
}: IFormSectionProps<D>) {
  const [ChildProvider, childContext, { validate }] = useValidation({
    ...rest,
    initialData,
    initialErrors: errors || initialErrors,
    name,
  });

  useEffect(() => {
    registerSelfValidatingChild!(name, validate);

    return () => unregisterSelfValidatingChild!(name);
  }, [registerSelfValidatingChild, unregisterSelfValidatingChild, name, validate]);

  return (
    <ChildProvider value={childContext}>
      {typeof children === 'function'
        ? children({
            ...rest,
            ...childContext,
          })
        : children}
    </ChildProvider>
  );
}

export default connectSectionToContext(FormSection);
